import { Box, CircularProgress, Typography } from '@mui/material';
import { FaceValidation } from 'idmission-web-sdk';
import { useNavigate } from 'react-router';

import { UserRefresher } from '@/components/AppRouter/UserRefresher';
import {
  IdmClassNames,
  IdmStyles,
} from '@/components/Idmission/Idmission.styled';
import { RejectedMessage } from '@/components/Idmission/RejectedMessage';

import { ROUTE } from '@/constants/routes';

import { useAppSelector } from '@/hooks/redux';
import { useIdmissionVerbiage } from '@/hooks/verification/useIdmissionVerbiage';
import { useIdmissionVerification } from '@/hooks/verification/useIdmissionVerification';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectUserHolderJointMinor } from '@/redux/user-data';

import {
  checkUserEkycStatus,
  isExistingCustomer,
  isRemediationCustomer,
} from '@/utils/user';

export function IdmissionFaceValidation() {
  const user = useAppSelector(selectLoggedInUser);
  const holderJointMinor = useAppSelector(selectUserHolderJointMinor);
  const minorClientId = holderJointMinor?.clientId;
  const { isSelfieChecked, isLivenessVideoRecorded } =
    checkUserEkycStatus(user);
  const navigate = useNavigate();
  const { verbiage } = useIdmissionVerbiage();

  const onCompleted = () => {
    if (isExistingCustomer(user) || isRemediationCustomer(user)) {
      navigate(ROUTE.MORE_INFORMATION);
    } else {
      navigate(ROUTE.BASIC_INFO);
    }
  };

  const {
    isProcessing,
    isCompleted,
    sessionId,
    idImageBase64,
    idImageLoading,
    isIDRejected,
    isSelfieRejected,
    isLivenessRejected,
    acknowledgeRejection,
    error,
    handleBeforeLivenessCheck,
    handleComplete,
    handleAcknowledgeRejection,
    resetVerification,
    handleOnRequestFailure,
  } = useIdmissionVerification({
    type: 'face',
    isSelfieChecked,
    isLivenessVideoRecorded,
    minorClientId,
    onCompleted,
  });

  if (
    (isIDRejected || isSelfieRejected || isLivenessRejected) &&
    !acknowledgeRejection
  ) {
    return (
      <RejectedMessage
        isIDRejected={isIDRejected}
        isSelfieRejected={isSelfieRejected}
        isLivenessRejected={isLivenessRejected}
        onAcknowledge={handleAcknowledgeRejection}
      />
    );
  }

  if (isProcessing || !sessionId || idImageLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isCompleted) {
    return <UserRefresher delayMs={3000} />;
  }

  return (
    <>
      {error && (
        <Typography color="error" variant="body2" mt={2}>
          {error}
        </Typography>
      )}
      <IdmStyles>
        <FaceValidation
          sessionId={sessionId}
          onBeforeSubmit={handleBeforeLivenessCheck}
          onComplete={handleComplete}
          onDenied={handleComplete}
          onExitCapture={resetVerification}
          onExitAfterFailure={resetVerification}
          onRequestFailure={handleOnRequestFailure}
          classNames={IdmClassNames.faceLiveness}
          verbiage={verbiage.faceLiveness}
          idCardForFaceMatch={idImageBase64 || undefined}
        />
      </IdmStyles>
    </>
  );
}
